/**
 * 模块: 竞品列表表格
 * 职责: 展示竞品列表和排序过滤功能
 * 限制: 文件<280行, 函数<30行
 */

import React from 'react';
import { Table, Tag, Tooltip, Button, Badge } from 'antd';
import type { CompetitorASIN } from '../../types';
import { getThreatLevelConfig } from './constants';

interface CompetitorTableProps {
  dataSource: CompetitorASIN[];
  loading: boolean;
  onViewDetail: (competitor: CompetitorASIN) => void;
}

export const CompetitorTable: React.FC<CompetitorTableProps> = ({
  dataSource,
  loading,
  onViewDetail,
}) => {
  const columns = [
    {
      title: 'ASIN',
      dataIndex: 'asin',
      key: 'asin',
      fixed: 'left' as const,
      width: 120,
      render: (asin: string) => (
        <a
          href={`https://www.amazon.com/dp/${asin}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {asin}
        </a>
      ),
    },
    {
      title: '品牌',
      dataIndex: 'brand',
      key: 'brand',
      width: 150,
      render: (brand: string) => brand || '-',
    },
    {
      title: '威胁等级',
      dataIndex: 'threat_level',
      key: 'threat_level',
      width: 120,
      filters: [
        { text: '高威胁', value: 'high' },
        { text: '中威胁', value: 'medium' },
        { text: '低威胁', value: 'low' },
      ],
      onFilter: (value: any, record: CompetitorASIN) => record.threat_level === value,
      render: (level: string) => {
        const config = getThreatLevelConfig(level);
        return (
          <Tooltip title={config.description}>
            <Tag color={config.color} icon={config.icon}>
              {config.text}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '出现次数',
      dataIndex: 'appearance_count',
      key: 'appearance_count',
      width: 100,
      sorter: (a: CompetitorASIN, b: CompetitorASIN) => a.appearance_count - b.appearance_count,
      render: (count: number) => (
        <Badge count={count} showZero style={{ backgroundColor: '#1890ff' }} />
      ),
    },
    {
      title: '首次出现',
      dataIndex: 'first_seen',
      key: 'first_seen',
      width: 120,
      sorter: (a: CompetitorASIN, b: CompetitorASIN) =>
        new Date(a.first_seen).getTime() - new Date(b.first_seen).getTime(),
    },
    {
      title: '最后出现',
      dataIndex: 'last_seen',
      key: 'last_seen',
      width: 120,
      sorter: (a: CompetitorASIN, b: CompetitorASIN) =>
        new Date(a.last_seen).getTime() - new Date(b.last_seen).getTime(),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 100,
      render: (_: any, record: CompetitorASIN) => (
        <Button type="link" size="small" onClick={() => onViewDetail(record)}>
          详情
        </Button>
      ),
    },
  ];

  return (
    <Table
      dataSource={dataSource}
      columns={columns}
      rowKey="id"
      loading={loading}
      scroll={{ x: 1000 }}
      pagination={{
        pageSize: 20,
        showSizeChanger: true,
        showTotal: (total) => `共 ${total} 个竞品`,
      }}
    />
  );
};

